/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useModal } from '../../../contexts/ModalContext'
import { eosApiService } from '../../../services/api/api.eos'
import { getPdfUrlInShowFile } from '../../../services/api/files'
import type { EosSiteInfo } from './types'
import ActionsMenu from '../../shared/ActionsMenu'
import AckViewDocDialog from '../../AckViewDocDialog'
import { validateFileFactory } from '../../../services/factory/validateFileFactory'
import { useServiceContext } from '../../../root'

interface TableMenuItemBase {
  studyFolderId?: number
  siteFolderId?: number
}

interface TableMenuProps {
  item: TableMenuItemBase
  itemKey: string | number
}

interface MenuItem {
  value: string
  action?: () => void
  disabled?: boolean
}

const LOADING_MENU_PLACEHOLDER: MenuItem = {
  value: 'LOADING...',
  disabled: true
}

const TableMenu: React.FC<TableMenuProps> = ({ item: siteDataObject, itemKey }) => {
  const { t } = useTranslation()
  const { showModal } = useModal()
  const context = useServiceContext()
  const [menuItems, setMenuItems] = useState<MenuItem[]>([LOADING_MENU_PLACEHOLDER])
  const [additionalSiteData, setAdditionalData] = useState<EosSiteInfo>({})

  // Stable refs to avoid re-creating callbacks on every render
  const validateFileRef = useRef(validateFileFactory())
  const showModalRef = useRef(showModal)
  showModalRef.current = showModal

  const folderId = siteDataObject.studyFolderId || siteDataObject.siteFolderId

  const loadSiteInfo = useCallback(() => {
    if (!folderId) return
    eosApiService
      .getSiteInfo(folderId as number)
      .then((siteData: unknown) => {
        setAdditionalData(siteData as EosSiteInfo)
      })
      .catch(() => {
        // Silently handle - menu items will reflect no data
      })
  }, [folderId])

  // Build menu items only when additionalSiteData or folderId actually change
  useEffect(() => {
    const tempMenuItems: MenuItem[] = []

    if (additionalSiteData.acknowledgmentDocumentId) {
      const acknowledgmentDocumentId = additionalSiteData.acknowledgmentDocumentId
      const thumbnailId = additionalSiteData.thumbnailId

      tempMenuItems.push({
        value: t('EOS_ACTION_VIEW_AND_PRINT_ACKNOWLEDGEMENT'),
        action: () => {
          showModalRef.current(
            <AckViewDocDialog
              pdfSrc={{
                service: () => {
                  return getPdfUrlInShowFile(acknowledgmentDocumentId, -1, 0, null)
                }
              }}
              pdfInfo={{
                fsiId: acknowledgmentDocumentId
              }}
              thumbnailId={thumbnailId?.toString()}
            />
          )
        }
      })

      tempMenuItems.push({
        value: t('EOS_ACTION_DOWNLOAD_ACKNOWLEDGEMENT'),
        action: () => {
          validateFileRef.current.downloadPdf(acknowledgmentDocumentId, true)
        }
      })
    }

    if (folderId) {
      tempMenuItems.push({
        value: t('EOS_ACTION_DOWNLOAD_EOS_MEDIA'),
        action: () => {
          eosApiService
            .downloadMedia([folderId as number], null)
            .then(() => {
              context.service.toast.success('EOS_MEDIA_DOWNLOAD_MEDIA_SUCCESS_MSG')
            })
            .catch((error) => {
              context.service.toast.error(error?.body?.message ?? error?.data?.message ?? '')
            })
        }
      })
    }

    setMenuItems(tempMenuItems)
  }, [additionalSiteData, folderId, t])

  useEffect(() => {
    loadSiteInfo()
  }, [loadSiteInfo])

  return (
    <ActionsMenu
      onToggle={(open: boolean) => {
        if (open) {
          loadSiteInfo()
        }
      }}
      menuId={`action-menu-tasks-${itemKey}`}
      items={menuItems}
    />
  )
}

export default TableMenu
